import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { HomeworkShell } from "./HomeworkShell";
import { hwApi, hwErr } from "./hwApi";

function TopicsInner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [openTopic, setOpenTopic] = useState("");

  useEffect(() => {
    hwApi.get("/syllabus").then(({ data: d }) => setData(d)).catch(() => setError("Could not load the syllabus."));
  }, []);

  const generate = async (chapter, topic, subtopic = "") => {
    setError("");
    setBusy(`${chapter}|${topic}|${subtopic}`);
    try {
      const { data: a } = await hwApi.post("/assignments/generate", { chapter, topic, subtopic });
      navigate(`/homework/assignments/${a.assignment_id}`);
    } catch (err) {
      setError(hwErr(err));
      setBusy("");
    }
  };

  if (!data) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;
  const enabled = data.topic_wise_enabled;

  return (
    <div className="tp-wrap" data-testid="hw-topics">
      <div className="tp-eyebrow">{data.category_label} SYLLABUS</div>
      <h1 className="tp-h2" style={{ fontSize: 30 }}>Topic-wise homework</h1>
      {enabled ? (
        <p className="tp-muted" data-testid="hw-topics-practice-note">
          Pick any topic — or drill into a subtopic — and get a fresh 10-question homework set on it.
          Select your MCQ options, upload your handwritten work, and Pranjal Sir personally checks every submission.</p>
      ) : (
        <p className="tp-muted" data-testid="hw-topics-mentor-note">Pranjal Sir assigns homework from these chapters. Your assignments appear under <b>My Homework</b> — with questions, deadlines and instructions set by him.</p>
      )}
      {error && <p className="tp-error" role="alert" data-testid="hw-topics-error">{error}</p>}
      {data.chapters.map((ch) => (
        <div className="tp-panel" style={{ marginTop: 14 }} key={ch.chapter} data-testid={`hw-chapter-${ch.chapter.replace(/\W+/g, "-")}`}>
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>{ch.chapter}</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            {ch.topics.map((t) => {
              const tKey = `${ch.chapter}|${t.topic}`;
              const isOpen = openTopic === tKey;
              const hasSubs = (t.subtopics || []).length > 0;
              if (!enabled) {
                return <span key={t.syllabus_id} className="tp-slot" data-testid={`hw-topic-${t.syllabus_id}`}>{t.topic}</span>;
              }
              return (
                <div key={t.syllabus_id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <button className="tp-slot" disabled={busy !== ""}
                          onClick={() => (hasSubs ? setOpenTopic(isOpen ? "" : tKey) : generate(ch.chapter, t.topic))}
                          data-testid={`hw-topic-${t.syllabus_id}`}
                          style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                                   border: isOpen ? "1.5px solid #1d4ed8" : undefined, background: isOpen ? "#eff6ff" : undefined }}>
                    {busy === `${tKey}|` ? "Preparing…" : `${t.topic} (${t.questions})`}
                    {hasSubs && (isOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />)}
                  </button>
                  {hasSubs && isOpen && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "8px 10px", background: "#f0f6ff",
                                  borderRadius: 10, border: "1px dashed #1d4ed8", maxWidth: 420 }}
                         data-testid={`hw-subtopics-${t.syllabus_id}`}>
                      <button className="tp-btn small" disabled={busy !== ""}
                              onClick={() => generate(ch.chapter, t.topic)}
                              data-testid={`hw-subtopic-all-${t.syllabus_id}`}>
                        {busy === `${tKey}|` ? "Preparing…" : `All of ${t.topic}`}
                      </button>
                      {t.subtopics.map((st) => (
                        <button key={st} className="tp-btn small ghost" disabled={busy !== ""}
                                onClick={() => generate(ch.chapter, t.topic, st)}
                                data-testid={`hw-subtopic-${t.syllabus_id}-${st.replace(/\W+/g, "-")}`}
                                style={{ background: "#fff" }}>
                          {busy === `${tKey}|${st}` ? "Preparing…" : st}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function HomeworkTopics() {
  return <HomeworkShell><TopicsInner /></HomeworkShell>;
}
